import { datadogLogs } from "@datadog/browser-logs"
import { datadogRum } from "@datadog/browser-rum"
import {
  nextjsPlugin,
  onRouterTransitionStart,
} from "@datadog/browser-rum-nextjs"
import { getEnvironment } from "@/lib/env/getEnvironment"
import getTenant from "@/lib/tenant/getTenant"

export { onRouterTransitionStart }

// Known browser-extension / email-scanner / benign-timing errors that otherwise
// dominate Error Tracking. SafeLinks: Microsoft Outlook & Office 365 link
// scanners inject a script that throws this verbatim — it is not our code.
// "ResizeObserver loop …" is a benign notice the browser emits, not a bug.
const RUM_ERROR_NOISE = [
  /Object Not Found Matching Id:\d+, MethodName:update, ParamCount:\d+/,
  /ResizeObserver loop (?:completed with undelivered notifications|limit exceeded)/,
]

const env = getEnvironment()
const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID
const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN

if (
  env !== "development" &&
  process.env.NEXT_PUBLIC_DISABLE_RUM !== "true" &&
  applicationId &&
  clientToken
) {
  // Default 100% to match the legacy ClientConsole app, which recorded replay on
  // ~all prod sessions — coaches/users expect that coverage. Override per deploy
  // via NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE (e.g. lower in staging).
  const sessionReplaySampleRate = Number(
    process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE ?? "100",
  )

  // Datadog's org-wide env tag is `prod` (the mobile app and other RUM apps use
  // it); our app-level env value is `production`, which drives UI chrome — the
  // env banner, favicon dot, title prefix — via getEnvironment(). Map only here,
  // at the Datadog boundary, so RUM + Logs line up with the rest of the org
  // without changing those UI semantics. `staging` already matches across apps.
  const datadogEnv = env === "production" ? "prod" : env

  const common = {
    clientToken,
    site: process.env.NEXT_PUBLIC_DATADOG_SITE ?? "us5.datadoghq.com",
    service: process.env.NEXT_PUBLIC_DATADOG_SERVICE ?? "web",
    env: datadogEnv,
    version: process.env.NEXT_PUBLIC_BUILD_VERSION,
  }

  datadogRum.init({
    applicationId,
    ...common,
    sessionReplaySampleRate,
    // Drop known third-party / benign noise so Error Tracking reflects real app
    // errors (see RUM_ERROR_NOISE). Returning false discards the event in-browser.
    beforeSend: (event) => {
      if (event.type === "error") {
        const message = event.error?.message ?? ""
        if (RUM_ERROR_NOISE.some((re) => re.test(message))) return false
      }
      return true
    },
    // Browser → same-origin /api/* routes. Datadog headers on these requests
    // get picked up by dd-trace on the server, linking RUM sessions to APM spans.
    allowedTracingUrls: [window.location.origin],
    propagateTraceBaggage: true,
    plugins: [nextjsPlugin()],
    // sessionSampleRate, trackUserInteractions, trackResources, trackLongTasks,
    // defaultPrivacyLevel, enablePrivacyForActionName, telemetrySampleRate,
    // traceSampleRate — all left to v7 defaults.
  })

  datadogLogs.init({
    ...common,
    forwardErrorsToLogs: false,
  })

  datadogRum.setGlobalContextProperty("tenant", getTenant())
  datadogLogs.setGlobalContextProperty("tenant", getTenant())
}
